// src/components/cooler/inventory/ItemTable.tsx

import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Typography,
  Tooltip,
  Box,
} from '@mui/material';
import {
  WrenchIcon,
  Trash2,
  Power,
  Euro,
  AlertTriangle,
  Image as ImageIcon,
  Tag as CategoryIcon,
  MapPin as LocationIcon,
  ChevronDown,
} from 'lucide-react';
import type { InventoryItem } from '../types/inventoryTypes';

// Category color mappings
const CATEGORY_COLORS: Record<string, { color: string; background: string }> = {
  Sound: { color: '#2563eb', background: '#dbeafe' },
  Lighting: { color: '#7c3aed', background: '#ede9fe' },
  Video: { color: '#be185d', background: '#fce7f3' },
  Power: { color: '#b45309', background: '#fef3c7' },
  Office: { color: '#047857', background: '#d1fae5' },
  Stage: { color: '#4f46e5', background: '#e0e7ff' },
  Cables: { color: '#dc2626', background: '#fee2e2' },
  Cases: { color: '#0369a1', background: '#e0f2fe' },
  Microphones: { color: '#059669', background: '#ecfdf5' },
  Cameras: { color: '#9333ea', background: '#f3e8ff' },
  Other: { color: '#374151', background: '#f4f4f5' },
  FOH: { color: '#d97706', background: '#fffaf0' },
  Rigging: { color: '#dc2626', background: '#fee2e2' },
};

type SortableField = 'name' | 'category' | 'primaryLocation' | 'quantity' | 'currentValue' | 'purchaseDate';
type SortDirection = 'asc' | 'desc';

interface ItemTableProps {
  items: InventoryItem[];
  onItemClick: (item: InventoryItem) => void;
  onMaintenanceClick: (item: InventoryItem) => void;
  onDelete?: (id: string) => void;
  onCategoryFilter?: (category: string) => void;
  onLocationFilter?: (location: string) => void;
  onShelfFilter?: (location: string, shelf: string) => void;
}

export const ItemTable: React.FC<ItemTableProps> = ({
  items,
  onItemClick,
  onMaintenanceClick,
  onDelete,
  onCategoryFilter,
  onLocationFilter,
  onShelfFilter,
}) => {
  const [sortField, setSortField] = useState<SortableField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedItems = React.useMemo(() => {
    return [...items].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle different data types
      if (sortField === 'currentValue') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else if (sortField === 'purchaseDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortField, sortDirection]);

  const getMaintenanceColor = (status?: string): "success" | "warning" | "error" => {
    switch (status) {
      case 'ok': return 'success';
      case 'due': return 'warning';
      case 'overdue': return 'error';
      default: return 'success';
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (onDelete && window.confirm(`Are you sure you want to delete "${name}"?`)) {
      onDelete(id);
    }
  };

  const renderImage = (item: InventoryItem) => {
    if (!item.images?.length) {
      return (
        <Avatar
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            width: 40,
            height: 40,
          }}
        >
          <ImageIcon size={20} color="gray" />
        </Avatar>
      );
    }

    return (
      <Avatar
        src={item.images[0].url}
        alt={item.name}
        sx={{
          width: 40,
          height: 40,
          '& img': {
            objectFit: 'cover',
          },
        }}
      />
    );
  };

  const renderLocation = (item: InventoryItem) => {
    return (
      <Stack direction="column" spacing={0.5}>
        <Tooltip title="Click to filter by this location">
          <Chip
            size="small"
            icon={<LocationIcon size={12} />}
            label={item.primaryLocation}
            clickable
            onClick={(e) => {
              e.stopPropagation();
              onLocationFilter?.(item.primaryLocation);
            }}
            sx={{
              fontSize: '0.75rem',
              height: 24,
              cursor: 'pointer',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                boxShadow: 1,
              },
            }}
          />
        </Tooltip>
        {item.secondLocation && (
          <Tooltip title={`Click to filter by ${item.primaryLocation} - ${item.secondLocation}`}>
            <Chip
              size="small"
              icon={<ChevronDown size={12} />}
              label={item.secondLocation}
              variant="outlined"
              clickable
              onClick={(e) => {
                e.stopPropagation();
                onShelfFilter?.(item.primaryLocation, item.secondLocation!);
              }}
              sx={{
                fontSize: '0.7rem',
                height: 20,
                cursor: 'pointer',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  boxShadow: 1,
                },
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderStatusChips = (item: InventoryItem) => {
    const chips = [];

    if (item.requiresPower) {
      chips.push(
        <Chip
          key="power"
          size="small"
          icon={<Power size={12} />}
          label="Power"
          color="warning"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      );
    }

    if (item.isInsured) {
      chips.push(
        <Chip
          key="insured"
          size="small"
          icon={<Euro size={12} />}
          label="Insured"
          color="success"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      );
    }

    if (item.maintenanceStatus === 'overdue') {
      chips.push(
        <Chip
          key="overdue"
          size="small"
          icon={<AlertTriangle size={12} />}
          label="Overdue"
          color="error"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      );
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
        {chips}
      </Stack>
    );
  };

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        maxHeight: 'calc(100vh - 300px)',
        backgroundColor: 'transparent',
        boxShadow: 'none'
      }}
    >
      <Table 
        stickyHeader 
        size="small" 
        sx={{ 
          minWidth: 1400, // Increased to ensure all content fits
          '& .MuiTableCell-root': {
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            py: 1.5,
            px: 2,
            backgroundColor: 'transparent'
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            fontWeight: 600,
            color: 'text.primary',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
            }
          }
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 60 }}>Image</TableCell>
            <TableCell sx={{ width: 250 }}>
              <TableSortLabel
                active={sortField === 'name'}
                direction={sortField === 'name' ? sortDirection : 'asc'}
                onClick={() => handleSort('name')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: 'text.primary !important'
                  }
                }}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: 120 }}>
              <TableSortLabel
                active={sortField === 'category'}
                direction={sortField === 'category' ? sortDirection : 'asc'}
                onClick={() => handleSort('category')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: 'text.primary !important'
                  }
                }}
              >
                Category
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: 160 }}>
              <TableSortLabel
                active={sortField === 'primaryLocation'}
                direction={sortField === 'primaryLocation' ? sortDirection : 'asc'}
                onClick={() => handleSort('primaryLocation')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: 'text.primary !important'
                  }
                }}
              >
                Location
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ width: 80 }}>
              <TableSortLabel
                active={sortField === 'quantity'}
                direction={sortField === 'quantity' ? sortDirection : 'asc'}
                onClick={() => handleSort('quantity')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: 'text.primary !important'
                  }
                }}
              >
                Qty
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: 140 }}>Status</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>
              <TableSortLabel
                active={sortField === 'currentValue'}
                direction={sortField === 'currentValue' ? sortDirection : 'asc'}
                onClick={() => handleSort('currentValue')}
                sx={{
                  '& .MuiTableSortLabel-icon': {
                    color: 'text.primary !important'
                  }
                }}
              >
                Value
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: 180 }}>Serial Number</TableCell>
            <TableCell 
              align="center" 
              sx={{ 
                width: 100,
                position: 'sticky',
                right: 0,
                backgroundColor: '#475569 !important',
                zIndex: 2,
                borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow
              key={item._id}
              hover
              onClick={() => onItemClick(item)}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'transparent'
              }}
            >
              <TableCell sx={{ width: 60 }}>{renderImage(item)}</TableCell>
              
              <TableCell sx={{ width: 250 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500} noWrap color="text.primary">
                    {item.name}
                  </Typography>
                  {item.description && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: 'block',
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 220
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
              </TableCell>

              <TableCell sx={{ width: 120 }}>
                <Tooltip title="Click to filter by this category">
                  <Chip
                    size="small"
                    icon={<CategoryIcon size={12} />}
                    label={item.category}
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategoryFilter?.(item.category);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      color: CATEGORY_COLORS[item.category]?.color || 'text.primary',
                      bgcolor: CATEGORY_COLORS[item.category]?.background || 'rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 1,
                      },
                    }}
                  />
                </Tooltip>
              </TableCell>

              <TableCell sx={{ width: 160 }}>{renderLocation(item)}</TableCell>

              <TableCell align="center" sx={{ width: 80 }}>
                <Typography variant="h6" color="primary.main" fontWeight="bold">
                  {item.quantity}
                </Typography>
              </TableCell>

              <TableCell sx={{ width: 140 }}>{renderStatusChips(item)}</TableCell>

              <TableCell align="right" sx={{ width: 100 }}>
                {item.currentValue && item.currentValue > 0 && (
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    €{item.currentValue.toLocaleString()}
                  </Typography>
                )}
              </TableCell>

              <TableCell sx={{ width: 180 }}>
                {item.serialNumber && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      display: 'block',
                      wordBreak: 'break-all',
                      lineHeight: 1.2
                    }}
                  >
                    {item.serialNumber}
                  </Typography>
                )}
              </TableCell>

              <TableCell 
                align="center" 
                sx={{ 
                  width: 100,
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#475569 !important',
                  zIndex: 1,
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="Maintenance">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMaintenanceClick(item);
                      }}
                      color={getMaintenanceColor(item.maintenanceStatus)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      <WrenchIcon size={16} />
                    </IconButton>
                  </Tooltip>
                  
                  {onDelete && (
                    <Tooltip title="Delete Item">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDelete(e, item._id, item.name)}
                        color="error"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                          }
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ItemTable;